/**
 * «Base de conhecimento» — seção `#base-de-conhecimento` do domínio IA & conduta
 * (spec `docs/superpowers/specs/2026-10-08-base-de-conhecimento-e-transporte-de-config-
 * design.md` §3).
 *
 * Até aqui as entradas (`knowledge_entries`) só nasciam por seed/script — nenhum caminho de
 * escrita pela API (medição da spec §1). Esta seção é a tela do CRUD novo
 * `/v1/knowledge-entries`.
 *
 * Gate em DUAS camadas (D-B):
 *   1. `DomainSection` (catálogo, `omitWhenBarred`) — sem `knowledge-base-author` a seção
 *      some inteira: a cap é de PAPEL (só `admin_tenant`), não de plano; não há upsell.
 *   2. Aqui, a mesma cap vai para o `enabled` da query: montada fora do hub, a seção não
 *      dispara um GET que voltaria 403 (gatear o FETCH, não só o render).
 *
 * Busca e filtros são no cliente: o contrato devolve a lista inteira (sem paginação), e
 * filtrar localmente responde a cada tecla sem request — o mesmo dobramento de acento do
 * Spotlight (`foldTerm`).
 *
 * Fidelidade: não há prancha do Claude Design para esta tela. Segue o idioma das seções
 * vizinhas do hub (cartão `rounded-xl border-border-subtle`, tabela de `ContaSaldoSection`)
 * e as classes do DS (`btn`, `input`, `badge`, `chip`, `empty-state`, `switch`) — nada de
 * estilo novo.
 */
import { Modal } from '@lume/ui-components';
import { useMemo, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';

import { contextoLabel, KIND_EXPLICACAO, KIND_LABELS, matchesSearch } from './knowledge-form';
import { KnowledgeEntryFormModal } from './KnowledgeEntryFormModal';
import { useDeleteKnowledgeEntry, useKnowledgeEntries } from './use-knowledge-entries';

import type { KnowledgeEntryDto, KnowledgeKind } from './types';

/** Palavras-chave visíveis por linha; o resto vira «+N» (a lista inteira fica no title). */
const MAX_KEYWORDS_NA_LISTA = 4;
export const KNOWLEDGE_BASE_CAPABILITY = 'knowledge-base-author';

type FiltroTipo = 'all' | KnowledgeKind;
type FiltroSituacao = 'all' | 'enabled' | 'disabled';

/** Diálogo aberto: nenhum · criar · editar X · excluir X. */
type Dialogo =
  | { tipo: 'nenhum' }
  | { tipo: 'criar' }
  | { tipo: 'editar'; entry: KnowledgeEntryDto }
  | { tipo: 'excluir'; entry: KnowledgeEntryDto };

export function KnowledgeBaseSection(): JSX.Element | null {
  const canAuthor = useCapability(KNOWLEDGE_BASE_CAPABILITY);
  const lista = useKnowledgeEntries(canAuthor);
  const excluir = useDeleteKnowledgeEntry();

  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState<FiltroTipo>('all');
  const [situacao, setSituacao] = useState<FiltroSituacao>('all');
  const [dialogo, setDialogo] = useState<Dialogo>({ tipo: 'nenhum' });
  const [erroExcluir, setErroExcluir] = useState<string | null>(null);

  const todas = lista.data?.items;
  const visiveis = useMemo(
    () =>
      (todas ?? []).filter(
        (e) =>
          (tipo === 'all' || e.kind === tipo) &&
          (situacao === 'all' || e.enabled === (situacao === 'enabled')) &&
          matchesSearch(e, busca),
      ),
    [todas, tipo, situacao, busca],
  );

  // D-B: sem a cap, nada — nem o cromo (o DomainSection já omite; isto cobre a montagem avulsa).
  if (!canAuthor) return null;

  const fecharDialogo = (): void => {
    setDialogo({ tipo: 'nenhum' });
  };

  const confirmarExclusao = (entry: KnowledgeEntryDto): void => {
    if (excluir.isPending) return;
    setErroExcluir(null);
    excluir.mutate(entry.id, {
      onSuccess: fecharDialogo,
      onError: () => {
        setErroExcluir('Não foi possível excluir a entrada. Tente de novo.');
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-text-secondary">
        <span className="font-medium text-text-primary">{KIND_LABELS.fact}</span> —{' '}
        {KIND_EXPLICACAO.fact}.{' '}
        <span className="font-medium text-text-primary">{KIND_LABELS.rule}</span> —{' '}
        {KIND_EXPLICACAO.rule}.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="field min-w-[220px] flex-1">
          <label className="field__label" htmlFor="kb-busca">
            Buscar
          </label>
          <input
            id="kb-busca"
            type="search"
            className="input"
            value={busca}
            placeholder="Título, texto ou palavra-chave"
            aria-label="Buscar na base de conhecimento"
            onChange={(e) => {
              setBusca(e.target.value);
            }}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="kb-filtro-tipo">
            Tipo
          </label>
          <select
            id="kb-filtro-tipo"
            className="input"
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as FiltroTipo);
            }}
          >
            <option value="all">Todos</option>
            <option value="fact">Fatos</option>
            <option value="rule">Condutas</option>
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="kb-filtro-situacao">
            Situação
          </label>
          <select
            id="kb-filtro-situacao"
            className="input"
            value={situacao}
            onChange={(e) => {
              setSituacao(e.target.value as FiltroSituacao);
            }}
          >
            <option value="all">Todas</option>
            <option value="enabled">Ativas</option>
            <option value="disabled">Inativas</option>
          </select>
        </div>
        <button
          type="button"
          className="btn btn--primary btn--md"
          onClick={() => {
            setDialogo({ tipo: 'criar' });
          }}
        >
          Nova entrada
        </button>
      </div>

      {lista.isError && todas === undefined ? (
        <p
          role="alert"
          className="rounded-xl border border-border-subtle bg-surface p-4 text-sm text-text-secondary"
        >
          Não foi possível carregar a base de conhecimento. Recarregue a página para tentar de novo.
        </p>
      ) : todas === undefined ? (
        <p className="text-sm text-text-secondary">Carregando a base de conhecimento…</p>
      ) : todas.length === 0 ? (
        <div className="empty-state rounded-xl border border-dashed border-border-subtle">
          <p className="empty-state__title">A base de conhecimento ainda está vazia</p>
          <p className="empty-state__desc">
            Cadastre o que a IA precisa saber sobre o seu negócio e como ela deve agir.
          </p>
          <button
            type="button"
            className="btn btn--secondary btn--md"
            onClick={() => {
              setDialogo({ tipo: 'criar' });
            }}
          >
            Criar a primeira entrada
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-text-secondary" aria-live="polite">
            {visiveis.length === todas.length
              ? `${String(todas.length)} ${todas.length === 1 ? 'entrada' : 'entradas'}`
              : `${String(visiveis.length)} de ${String(todas.length)} entradas`}
          </p>
          {visiveis.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border-subtle p-4 text-sm text-text-secondary">
              Nenhuma entrada corresponde à busca ou aos filtros.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border-subtle bg-surface">
              {/* table-fixed + larguras: a seção mede ~704px no hub (3 colunas). Tipo, contexto e
                  prioridade vivem na célula do título e as palavras-chave mostram até 4 — sem isso
                  a tabela pedia 906px e as ações só apareciam rolando de lado (validação no
                  Chrome, 08/Out/2026). */}
              <table
                className="w-full table-fixed text-sm"
                aria-label="Entradas da base de conhecimento"
              >
                <thead>
                  <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-text-secondary">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Entrada
                    </th>
                    <th scope="col" className="w-[30%] px-4 py-2 font-medium">
                      Palavras-chave
                    </th>
                    <th scope="col" className="w-[88px] px-2 py-2 font-medium">
                      Situação
                    </th>
                    <th scope="col" className="w-[148px] px-2 py-2 font-medium">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map((e) => (
                    <tr key={e.id} className="border-b border-border-subtle last:border-b-0">
                      <td className="px-4 py-2 align-top">
                        <p className="truncate font-medium text-text-primary" title={e.title}>
                          {e.title}
                        </p>
                        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-secondary">
                          <span
                            className={e.kind === 'rule' ? 'badge badge--ai' : 'badge badge--info'}
                          >
                            {KIND_LABELS[e.kind]}
                          </span>
                          <span>{contextoLabel(e.vertical)}</span>
                          <span className="tabular-nums">· prioridade {e.priority}</span>
                        </p>
                      </td>
                      <td className="px-4 py-2 align-top">
                        {e.keywords.length === 0 ? (
                          <span className="text-xs text-text-secondary">—</span>
                        ) : (
                          <ul className="flex flex-wrap gap-1" title={e.keywords.join(', ')}>
                            {e.keywords.slice(0, MAX_KEYWORDS_NA_LISTA).map((k) => (
                              <li key={k} className="badge badge--neutral">
                                {k}
                              </li>
                            ))}
                            {e.keywords.length > MAX_KEYWORDS_NA_LISTA && (
                              <li className="text-xs text-text-secondary">
                                +{e.keywords.length - MAX_KEYWORDS_NA_LISTA}
                              </li>
                            )}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <span
                          className={e.enabled ? 'badge badge--success' : 'badge badge--neutral'}
                        >
                          {e.enabled ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right align-top">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          aria-label={`Editar «${e.title}»`}
                          onClick={() => {
                            setDialogo({ tipo: 'editar', entry: e });
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          aria-label={`Excluir «${e.title}»`}
                          onClick={() => {
                            setErroExcluir(null);
                            setDialogo({ tipo: 'excluir', entry: e });
                          }}
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {(dialogo.tipo === 'criar' || dialogo.tipo === 'editar') && (
        <KnowledgeEntryFormModal
          // A chave remonta o form ao trocar de entrada: estado sempre nasce da entrada certa.
          key={dialogo.tipo === 'editar' ? dialogo.entry.id : 'nova'}
          entry={dialogo.tipo === 'editar' ? dialogo.entry : null}
          onClose={fecharDialogo}
        />
      )}

      {dialogo.tipo === 'excluir' && (
        <Modal
          isOpen
          destructive
          onClose={fecharDialogo}
          title="Excluir entrada"
          confirmLabel={excluir.isPending ? 'Excluindo…' : 'Excluir entrada'}
          cancelLabel="Cancelar"
          onConfirm={() => {
            confirmarExclusao(dialogo.entry);
          }}
        >
          <p className="text-sm text-text-primary">
            Excluir <strong>«{dialogo.entry.title}»</strong>? A IA deixa de usar esta entrada e ela
            não pode ser recuperada.
          </p>
          <p className="mt-2 text-xs text-text-secondary">
            Se for só uma pausa, edite a entrada e desligue «Entrada ativa».
          </p>
          {erroExcluir !== null && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {erroExcluir}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}
